import { ReactNode } from "react";
import { Button, Paper, Skeleton, Typography } from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";

interface AsyncSectionProps {
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  skeletonHeight: number;
  errorMessage?: string;
  children: ReactNode;
}

// One query's worth of the page: skeleton while in flight, an inline
// error + retry button if it failed, or the real content - all scoped to
// just this section, so one slow/failed query never blocks (or hides) the
// rest of the tab. Used by Brands.tsx (desktop and mobile).
export default function AsyncSection({
  isLoading,
  isError,
  onRetry,
  skeletonHeight,
  errorMessage,
  children,
}: AsyncSectionProps) {
  if (isError) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          textAlign: "center",
          borderColor: "#ececec",
          minHeight: skeletonHeight,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
        }}
      >
        <Typography sx={{ color: "#757575", fontSize: 13.5 }}>
          {errorMessage ?? "Something went wrong loading this section."}
        </Typography>
        <Button variant="outlined" color="secondary" size="small" startIcon={<ReplayIcon />} onClick={onRetry}>
          Retry
        </Button>
      </Paper>
    );
  }

  if (isLoading) {
    return <Skeleton variant="rounded" height={skeletonHeight} />;
  }

  return <>{children}</>;
}
